// Square cursor v2 — state system from design spec (does not replace CustomCursor.js).
// States: DEFAULT · LINK/BUTTON · TEXT (bar) · PRESS · DRAG · TOUCH (off).
//
// DRAG: add data-cursor="drag" on carousel / draggable regions.
// Touch / coarse pointer: component unmounts; native cursor stays.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

const SPECS = {
  default: { w: 10, h: 10, opacity: 1 },
  link: { w: 22, h: 22, opacity: 0.42 },
  text: { w: 2, h: 20, opacity: 1 },
  drag: { w: 14, h: 14, opacity: 1 },
};

const PRESS_DURATION_MS = 120;
const PRESS_SCALE = 0.6;

function isCoarseOrNoHover() {
  if (typeof window === "undefined") return true;
  return window.matchMedia("(pointer: coarse)").matches;
}

function chainFromElement(el) {
  const chain = [];
  let n = el;
  while (n && n.nodeType === 1 && n !== document.documentElement) {
    chain.push(n);
    n = n.parentElement;
  }
  return chain;
}

function isInteractive(node) {
  if (!node || node.nodeType !== 1) return false;
  const tag = node.tagName;
  if (tag === "A" || tag === "BUTTON" || tag === "SUMMARY") return true;
  if (node.getAttribute("role") === "button") return true;
  if (node.classList?.contains("clickable")) return true;
  if (node.getAttribute("data-cursor") === "pointer") return true;
  if (
    tag === "INPUT" &&
    ["submit", "button", "reset", "checkbox", "radio", "file"].includes(
      node.type?.toLowerCase() || ""
    )
  ) {
    return true;
  }
  if (tag === "SELECT") return true;
  return false;
}

function isTextual(node) {
  if (!node || node.nodeType !== 1) return false;
  const tag = node.tagName;
  if (["P", "H1", "H2", "H3", "H4", "H5", "H6", "LI", "TD", "TH", "LABEL", "BLOCKQUOTE", "FIGCAPTION", "PRE", "CODE"].includes(tag)) {
    return true;
  }
  if (tag === "INPUT") {
    const t = node.type?.toLowerCase() || "";
    return ["text", "email", "search", "url", "tel", "password", "number"].includes(t);
  }
  if (tag === "TEXTAREA") return true;
  try {
    if (getComputedStyle(node).cursor === "text") return true;
  } catch {
    /* ignore */
  }
  return false;
}

function resolveState(el) {
  if (!el || el.nodeType !== 1) return "default";
  const chain = chainFromElement(el);

  for (const node of chain) {
    if (node.getAttribute?.("data-cursor") === "drag") return "drag";
  }
  for (const node of chain) {
    if (isInteractive(node)) return "link";
  }
  for (const node of chain) {
    if (isTextual(node)) return "text";
  }
  return "default";
}

/**
 * Custom cursor matching CURSOR STATES spec — desktop, fine pointer only.
 */
const CustomCursorV2 = () => {
  const [useCustom, setUseCustom] = useState(() => !isCoarseOrNoHover());
  const [x, setX] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth / 2 : 0
  );
  const [y, setY] = useState(() =>
    typeof window !== "undefined" ? window.innerHeight / 2 : 0
  );
  const [stateKey, setStateKey] = useState("default");
  const [pressing, setPressing] = useState(false);
  const [isDark, setIsDark] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : false
  );
  const pressTimerRef = useRef(null);
  const location = useLocation();
  const innerRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(pointer: coarse)");
    const onChange = () => setUseCustom(!mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const el = document.documentElement;
    const sync = () => setIsDark(el.classList.contains("dark"));
    sync();
    const mo = new MutationObserver(sync);
    mo.observe(el, { attributes: true, attributeFilter: ["class"] });
    return () => mo.disconnect();
  }, []);

  const applyGlobalHide = useCallback(() => {
    const id = "custom-cursor-v2-global-hide";
    let style = document.getElementById(id);
    if (!style) {
      style = document.createElement("style");
      style.id = id;
      document.head.appendChild(style);
    }
    style.textContent = `
      html.has-custom-cursor-v2, html.has-custom-cursor-v2 * {
        cursor: none !important;
      }
    `;
    document.documentElement.classList.add("has-custom-cursor-v2");
  }, []);

  const removeGlobalHide = useCallback(() => {
    document.documentElement.classList.remove("has-custom-cursor-v2");
    const style = document.getElementById("custom-cursor-v2-global-hide");
    if (style) style.remove();
  }, []);

  const onPointerMove = useCallback((e) => {
    if (document.body.getAttribute("data-loading") === "true") {
      setX(window.innerWidth / 2);
      setY(window.innerHeight / 2);
      return;
    }
    setX(e.clientX);
    setY(e.clientY);
    const under = document.elementFromPoint(e.clientX, e.clientY);
    setStateKey(resolveState(under));
  }, []);

  const onPointerDown = useCallback(() => {
    if (pressTimerRef.current) clearTimeout(pressTimerRef.current);
    setPressing(true);
    pressTimerRef.current = window.setTimeout(() => {
      setPressing(false);
      pressTimerRef.current = null;
    }, PRESS_DURATION_MS);
  }, []);

  const onPointerUp = useCallback(() => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
    setPressing(false);
  }, []);

  useEffect(() => {
    if (!useCustom) {
      removeGlobalHide();
      return undefined;
    }

    applyGlobalHide();

    const loadingCenter = () => {
      if (document.body.getAttribute("data-loading") === "true") {
        setX(window.innerWidth / 2);
        setY(window.innerHeight / 2);
      }
    };
    loadingCenter();

    window.addEventListener("pointermove", onPointerMove, { passive: true });
    window.addEventListener("pointerdown", onPointerDown, { passive: true });
    window.addEventListener("pointerup", onPointerUp, { passive: true });
    window.addEventListener("blur", onPointerUp);
    window.addEventListener("resize", loadingCenter);

    const mo = new MutationObserver(() => {
      applyGlobalHide();
    });
    mo.observe(document.body, { attributes: true, attributeFilter: ["data-loading"] });

    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("blur", onPointerUp);
      window.removeEventListener("resize", loadingCenter);
      mo.disconnect();
      removeGlobalHide();
      if (pressTimerRef.current) clearTimeout(pressTimerRef.current);
    };
  }, [useCustom, applyGlobalHide, removeGlobalHide, onPointerMove, onPointerDown, onPointerUp]);

  useEffect(() => {
    setStateKey("default");
    setPressing(false);
  }, [location.pathname]);

  if (!useCustom) return null;

  const spec = SPECS[stateKey] || SPECS.default;

  let backgroundColor;
  if (stateKey === "link") {
    backgroundColor = isDark ? "rgba(230, 230, 230, 0.55)" : "rgba(40, 40, 40, 0.45) blur(16px)";
  } else if (stateKey === "text") {
    backgroundColor = isDark ? "rgb(255, 255, 255)" : "rgb(0, 0, 0)";
  } else {
    backgroundColor = isDark ? "rgb(255, 255, 255)" : "rgb(0, 0, 0)";
  }

  const opacity = spec.opacity;
  const scale = pressing ? PRESS_SCALE : 1;

  const innerStyle = {
    width: spec.w,
    height: spec.h,
    opacity,
    backgroundColor,
    transform: `scale(${scale})`,
    transition:
      "width 0.15s ease-out, height 0.15s ease-out, opacity 0.15s ease-out, background-color 0.15s ease-out, transform 0.12s ease-out",
    transformOrigin: "center center",
  };

  if (stateKey === "link") {
    innerStyle.backdropFilter = "blur(10px)";
    innerStyle.WebkitBackdropFilter = "blur(10px)";
  }

  return (
    <div
      className="fixed left-0 top-0 pointer-events-none z-[99999]"
      style={{
        transform: `translate(${x}px, ${y}px) translate(-50%, -50%)`,
        willChange: "transform",
      }}
      aria-hidden
    >
      <div ref={innerRef} className="rounded-none" style={innerStyle} />
    </div>
  );
};

export default CustomCursorV2;
